import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Menu, X, Shield, ChevronDown, Sun, Moon } from 'lucide-react';

const navLinks = [
  { name: 'Home', href: '/', isHash: true, sectionId: '#home' },
  { name: 'Services', href: '/', isHash: true, sectionId: '#services' },
  { name: 'Security', href: '/security' },
  { name: 'Why Us', href: '/', isHash: true, sectionId: '#why-us' },
  { name: 'Careers', href: '/careers' },
  { name: 'Testimonials', href: '/', isHash: true, sectionId: '#testimonials' },
  { name: 'Blog', href: '/blog/en' },
];

const FULL_LOGO = '/images/NewLogo1.png';
const FULL_LOGO_DARK = '/images/Logo1.png';
const SMALL_LOGO = '/images/Hakxcore_logo_insta.png';

export const Navbar = ({ theme, toggleTheme, isBannerVisible, bannerHeight, transparent = true }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('#home');
  const navigate = useNavigate();
  const location = useLocation();

  const isLightTheme = theme === 'light';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Track active section using Intersection Observer
  useEffect(() => {
    if (location.pathname !== '/') return;

    const sectionIds = navLinks
      .filter(link => link.isHash && link.sectionId)
      .map(link => link.sectionId.replace('#', ''));

    const observer = new IntersectionObserver(
      (entries) => {
        // Find the entry that is most visible
        const visibleEntries = entries.filter(entry => entry.isIntersecting);
        if (visibleEntries.length > 0) {
          // Get the one with the highest intersection ratio
          const mostVisible = visibleEntries.reduce((prev, current) =>
            current.intersectionRatio > prev.intersectionRatio ? current : prev
          );
          setActiveSection(`#${mostVisible.target.id}`);
        }
      },
      {
        rootMargin: '-10% 0px -40% 0px',
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5]
      }
    );

    // Delay to ensure lazy-loaded sections are in the DOM
    const timeoutId = setTimeout(() => {
      sectionIds.forEach((id) => {
        const element = document.getElementById(id);
        if (element) {
          observer.observe(element);
        }
      });
    }, 500);

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [location.pathname]);

  // Helper function to check if a link is active
  const isLinkActive = (link) => {
    if (link.external) return false;

    // For non-home pages, check pathname
    if (!link.isHash) {
      return location.pathname === link.href;
    }

    // For hash links on home page, check active section
    if (location.pathname === '/' && link.isHash) {
      return activeSection === link.sectionId;
    }

    return false;
  };

  const handleNavClick = (e, link) => {
    if (link.external) return;

    // For /blog/* paths, use full page navigation (handled by Vercel rewrites)
    if (link.href.startsWith('/blog')) {
      setIsOpen(false);
      window.location.href = link.href;
      return;
    }

    e.preventDefault();
    setIsOpen(false);

    if (link.isHash) {
      if (location.pathname === '/') {
        const element = document.querySelector(link.sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        navigate('/', { state: { scrollTo: link.sectionId } });
      }
    } else {
      navigate(link.href);
      window.scrollTo(0, 0);
    }
  };

  // Handle scroll after navigation to home
  useEffect(() => {
    if (location.pathname === '/' && location.state?.scrollTo) {
      const element = document.querySelector(location.state.scrollTo);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
      // Clear state to avoid scrolling on subsequent re-renders
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Determine if navbar should use light styling (white bg with dark text)
  // Only use light styling when: light theme AND (scrolled OR not transparent)
  const useLightNavbar = isLightTheme && (isScrolled || !transparent);

  const headerBg = isScrolled || !transparent
    ? isLightTheme
      ? 'bg-white/95 backdrop-blur-lg shadow-lg border-b border-border'
      : 'bg-dark-bg/95 backdrop-blur-lg shadow-lg border-b border-dark-border'
    : 'bg-transparent';

  return (
    <header
      className={`fixed left-0 right-0 z-50 transition-all duration-300 ${headerBg}`}
      style={{ top: isBannerVisible && !isScrolled ? `${bannerHeight}px` : '0' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link
            to="/"
            onClick={(e) => handleNavClick(e, { isHash: true, sectionId: '#home' })}
            className="flex items-center gap-2 group"
          >
            <img
              src={useLightNavbar ? FULL_LOGO_DARK : FULL_LOGO}
              alt="Hakxcore Logo"
              width="168"
              height="56"
              fetchpriority="high"
              className="h-12 md:h-14 w-auto hidden sm:block hover:opacity-90 transition-opacity"
            />
            <img
              src={SMALL_LOGO}
              alt="Hakxcore Logo"
              width="40"
              height="40"
              fetchpriority="high"
              className="h-10 w-10 sm:hidden rounded-lg shadow-glow-brand hover:opacity-90 transition-opacity"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              link.external ? (
                <a
                  key={link.name}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`px-4 py-2 text-sm font-medium transition-colors ${useLightNavbar
                      ? 'text-muted-foreground hover:text-foreground'
                      : 'text-dark-muted hover:text-dark-text'
                    }`}
                >
                  {link.name}
                </a>
              ) : (
                <Link
                  key={link.name}
                  to={link.href}
                  onClick={(e) => handleNavClick(e, link)}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${useLightNavbar
                      ? isLinkActive(link) ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
                      : isLinkActive(link) ? 'text-dark-text' : 'text-dark-muted hover:text-dark-text'
                    }`}
                >
                  {link.name}
                </Link>
              )
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <Button
              variant={useLightNavbar ? "ghost" : "dark-ghost"}
              size="icon"
              onClick={toggleTheme}
              className="mr-2"
            >
              {theme === 'dark' ? (
                <Sun className={`w-5 h-5 ${useLightNavbar ? 'text-foreground' : 'text-dark-text'}`} />
              ) : (
                <Moon className={`w-5 h-5 ${useLightNavbar ? 'text-foreground' : 'text-dark-text'}`} />
              )}
            </Button>
            <Button
              variant={useLightNavbar ? "ghost" : "dark-ghost"}
              size="default"
              className={useLightNavbar ? "text-muted-foreground hover:text-foreground" : "text-dark-muted hover:text-dark-text"}
              onClick={() => {
                if (location.pathname === '/') {
                  document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
                } else {
                  navigate('/', { state: { scrollTo: '#contact' } });
                }
              }}
            >
              Contact Us
            </Button>
            <Button
              variant="brand"
              size="default"
              onClick={() => window.open('https://calendar.app.google/TzJ8GRKhzddT7Kqp9', '_blank')}
              className="group"
            >
              Book Consultation
              <ChevronDown className="w-4 h-4 rotate-[-90deg] group-hover:translate-x-0.5 transition-transform" />
            </Button>
          </div>

          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button
                variant={useLightNavbar ? "ghost" : "dark-ghost"}
                size="icon"
                className={useLightNavbar
                  ? "bg-white/70 backdrop-blur-sm border border-border shadow-sm hover:bg-white/90"
                  : "bg-dark-bg/40 backdrop-blur-md border border-dark-border shadow-lg hover:bg-dark-bg/60"
                }
              >
                <Menu className={`w-6 h-6 ${useLightNavbar ? 'text-foreground' : 'text-dark-text'}`} />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className={`w-[300px] p-0 ${isLightTheme ? 'bg-white border-border' : 'bg-dark-bg border-dark-border'}`}>
              <div className="flex flex-col h-full">
                <div className={`flex items-center justify-between p-4 pr-12 border-b ${isLightTheme ? 'border-border' : 'border-dark-border'}`}>
                  <img
                    src={isLightTheme ? FULL_LOGO_DARK : FULL_LOGO}
                    alt="Hakxcore Logo"
                    width="96"
                    height="32"
                    loading="lazy"
                    className="h-8 w-auto"
                  />
                  <Button
                    variant={isLightTheme ? "ghost" : "dark-ghost"}
                    size="icon-sm"
                    onClick={toggleTheme}
                  >
                    {theme === 'dark' ? <Sun className={`w-4 h-4 ${isLightTheme ? 'text-foreground' : 'text-dark-text'}`} /> : <Moon className={`w-4 h-4 ${isLightTheme ? 'text-foreground' : 'text-dark-text'}`} />}
                  </Button>
                </div>

                <nav className="flex-1 py-6 px-4">
                  <div className="space-y-1">
                    {navLinks.map((link) => (
                      link.external ? (
                        <a
                          key={link.name}
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`flex items-center px-4 py-3 text-base font-medium rounded-lg transition-colors ${isLightTheme
                              ? 'text-muted-foreground hover:text-foreground hover:bg-muted'
                              : 'text-dark-muted hover:text-dark-text hover:bg-dark-elevated'
                            }`}
                        >
                          {link.name}
                        </a>
                      ) : (
                        <Link
                          key={link.name}
                          to={link.href}
                          onClick={(e) => handleNavClick(e, link)}
                          className={`flex items-center px-4 py-3 text-base font-medium rounded-lg transition-colors ${isLightTheme
                              ? isLinkActive(link) ? 'text-foreground bg-muted' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                              : isLinkActive(link) ? 'text-dark-text bg-dark-elevated' : 'text-dark-muted hover:text-dark-text hover:bg-dark-elevated'
                            }`}
                        >
                          {link.name}
                        </Link>
                      )
                    ))}
                  </div>
                </nav>

                <div className={`p-4 border-t space-y-3 ${isLightTheme ? 'border-border' : 'border-dark-border'}`}>
                  <Button
                    variant={isLightTheme ? "outline" : "dark-outline"}
                    className="w-full"
                    onClick={() => {
                      setIsOpen(false);
                      if (location.pathname === '/') {
                        document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
                      } else {
                        navigate('/', { state: { scrollTo: '#contact' } });
                      }
                    }}
                  >
                    Contact Us
                  </Button>
                  <Button
                    variant="brand"
                    className="w-full"
                    onClick={() => window.open('https://calendar.app.google/TzJ8GRKhzddT7Kqp9', '_blank')}
                  >
                    Book Consultation
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
